import type { FC } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip';
import { useCommandPaletteStore } from '@features/command-palette/store';
import clsx from 'clsx';
import { Search } from 'lucide-react';

import { navTooltipClasses } from './MenuNavItems';

interface NavSearchFieldProps {
  isCollapsed: boolean;
}

const SEARCH_LABEL = 'Search';

const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');
const MODIFIER_KEY = isMac ? '⌘' : 'Ctrl';

const kbdClasses =
  'bg-info-600 text-info-300 pointer-events-none inline-flex h-5 min-w-5 items-center justify-center rounded-sm px-1 font-sans text-xs font-medium select-none';

const ShortcutHint: FC = () => (
  <span className="inline-flex items-center gap-1">
    <kbd className={kbdClasses}>{MODIFIER_KEY}</kbd>
    <kbd className={kbdClasses}>K</kbd>
  </span>
);

export const NavSearchField: FC<NavSearchFieldProps> = ({ isCollapsed }) => {
  const open = useCommandPaletteStore(({ open }) => open);
  const field = (
    <button
      type="button"
      onClick={open}
      aria-label={SEARCH_LABEL}
      className={clsx(
        'text-info-500 border-info-600 mx-auto mt-5 flex h-10 shrink-0 cursor-pointer items-center rounded-md border bg-transparent',
        isCollapsed ? 'w-10 justify-center' : 'w-[250px] gap-3 px-3',
      )}
    >
      <Search size={16} strokeWidth={1.5} aria-hidden="true" />
      {!isCollapsed && (
        <span className="grow text-left text-sm leading-5 font-medium whitespace-nowrap">{SEARCH_LABEL}</span>
      )}
    </button>
  );

  if (!isCollapsed) {
    return field;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{field}</TooltipTrigger>
      <TooltipContent side="right" className={clsx(navTooltipClasses, 'flex items-center gap-2')}>
        {SEARCH_LABEL}
        <ShortcutHint />
      </TooltipContent>
    </Tooltip>
  );
};
